import type { ReactNode } from 'react';
import { Skeleton } from '@vnetwork/ui/primitives';

/** Deterministic initials (max 2 chars) from a person / document name. */
export function initialsOf(name?: string): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

/** Stable accent hue for an avatar, derived from the name string. */
function hueOf(name?: string): number {
  const s = name ?? '';
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}

/** Circular initials avatar (no Avatar primitive — token-styled span). */
export function InitialsAvatar({ name, size = 34 }: { name?: string; size?: number }) {
  const hue = hueOf(name);
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 'var(--vn-radius-full)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 'var(--vn-font-size-xs)',
        fontWeight: 'var(--vn-font-weight-semibold)',
        color: `hsl(${hue} 55% 32%)`,
        background: `hsl(${hue} 70% 92%)`,
      }}
    >
      {initialsOf(name)}
    </span>
  );
}

/** A single scrollable feed row: leading slot, title, clamped detail, meta. */
export function FeedRow({
  leading,
  title,
  detail,
  meta,
  trailing,
}: {
  leading: ReactNode;
  title: ReactNode;
  detail?: ReactNode;
  meta?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--vn-space-3)',
        padding: 'var(--vn-space-3) var(--vn-space-4)',
        borderTop: '1px solid var(--vn-border-subtle)',
      }}
    >
      <span style={{ marginTop: 'var(--vn-space-1)' }}>{leading}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 'var(--vn-font-weight-medium)',
            fontSize: 'var(--vn-font-size-sm)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </div>
        {detail ? (
          <div
            style={{
              fontSize: 'var(--vn-font-size-xs)',
              color: 'var(--vn-fg-muted)',
              marginTop: 'var(--vn-space-1)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {detail}
          </div>
        ) : null}
        {meta ? (
          <div
            style={{
              fontSize: 'var(--vn-font-size-xs)',
              color: 'var(--vn-fg-subtle)',
              marginTop: 'var(--vn-space-1)',
            }}
          >
            {meta}
          </div>
        ) : null}
      </div>
      {trailing ? <span style={{ flexShrink: 0 }}>{trailing}</span> : null}
    </div>
  );
}

/** Vertically scrollable list container for feed rows (fixed max height). */
export function FeedScroll({ children }: { children: ReactNode }) {
  /* maxHeight: 22.5rem ≈ 360px; no token maps to 360px */
  return <div style={{ maxHeight: '22.5rem', overflowY: 'auto' }}>{children}</div>;
}

/** Loading skeleton — a few stacked feed-row placeholders. */
export function FeedSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div style={{ padding: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-3)' }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: 'flex', gap: 'var(--vn-space-3)' }}>
          <Skeleton style={{ width: 34, height: 34, borderRadius: 'var(--vn-radius-full)' }} />
          <div style={{ flex: 1, display: 'grid', gap: 'var(--vn-space-1)' }}>
            <Skeleton style={{ height: 12, width: '70%' }} />
            <Skeleton style={{ height: 10, width: '90%' }} />
          </div>
        </div>
      ))}
    </div>
  );
}
